import { cn } from '@lib/utils';
import { baseRowStyle, baseTableHeaderStyle } from './style';
import TableCell from './table-cell';
import { ColumnRenderInfo } from './type';

interface TableProps<T> {
  columnRenderInfoList: ColumnRenderInfo<T>[];
  dataList: T[];
  className?: string;
  noResultLabel?: React.ReactNode;
}

function Table<T extends Record<string, string>>({
  columnRenderInfoList,
  dataList,
  className,
  noResultLabel,
}: TableProps<T>) {
  const isEmpty = dataList.length === 0;

  return (
    <div
      className={cn(
        'relative rounded-lg border border-solid border-3b-background-200 text-sm',
        className,
      )}
    >
      <table className="w-full text-3b-text-primary">
        <thead className="block h-[48px] w-full bg-3b-background-medium font-semibold">
          <tr className="flex h-full w-full">
            {columnRenderInfoList.map(column => (
              <th
                key={column.key as string}
                scope="col"
                className={cn(
                  baseTableHeaderStyle,
                  column.headerStyle ?? 'flex-1',
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="w-full">
          {dataList.map(row => (
            <tr
              key={`row-${row[columnRenderInfoList[0].key]}`}
              className={cn(baseRowStyle, 'flex w-full')}
            >
              {columnRenderInfoList.map((columnRenderInfo, colIndex) => (
                <TableCell
                  key={`cell-${colIndex}-${columnRenderInfo.key}`}
                  columnRenderInfo={columnRenderInfo}
                  row={row}
                  className={columnRenderInfo.cellStyle ?? 'flex-1'}
                />
              ))}
            </tr>
          ))}
          {isEmpty && (
            <tr className="flex w-full">
              <td colSpan={columnRenderInfoList.length} className="w-full">
                {noResultLabel ?? (
                  <div
                    className="py-[80px] text-center text-base font-medium leading-6
                tracking-[-0.176px] text-3b-text-secondary"
                  >
                    No results found.
                  </div>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
